import {
  ANCHOR_LABEL_FIELDS,
  MAX_TASK_ANCHOR_FIELDS,
  buildTaskColumnApiField,
  parseTaskColumnPath,
  type ColumnConfigEntry,
  type SystemEntity,
  type TaskColumnSource,
  type TaskOutcomeField,
} from '@erp/shared-types';
import type { TaskAnchorFieldCatalogEntry, TaskTypeResponse } from '@erp/rpc';

/**
 * Pure helpers for the task-page "Add column" picker and the admin default
 * columns editor, kept free of React imports so they stay unit-testable
 * (mirrors taskQueueCellFormat.ts).
 */

/**
 * A picker format: a stored column format, or `date_medium` (a `date` column
 * with `dateStyle: 'medium'`, "Oct 16, 2026").
 */
export type ColumnFormat =
  NonNullable<ColumnConfigEntry['format']> | 'date_medium';

export type TaskColumnFieldOption = {
  /** Dotted path below the source (e.g. `supplier.name`). */
  path: string;
  label: string;
  format?: ColumnFormat;
};

type InputSchemaField = NonNullable<
  TaskTypeResponse['inputSchema']
>['fields'][number];

const NUMERIC_FORMATS: ReadonlySet<ColumnFormat> = new Set([
  'number',
  'integer',
  'float',
  'currency',
  'currency4',
]);

function inputFieldFormat(fieldType: string): ColumnFormat | undefined {
  switch (fieldType) {
    case 'currency':
      return 'currency';
    case 'integer':
      return 'integer';
    case 'numeric':
    case 'number':
    case 'decimal':
    case 'float':
      return 'number';
    case 'date':
    case 'datetime':
      return 'date';
    default:
      return undefined;
  }
}

/**
 * Flatten a task type's input schema into selectable paths. Object fields
 * appear themselves (rendered compactly) and via their children; repeated
 * fields stop there since a path cannot pick one array element by name.
 */
export function inputSchemaFieldOptions(
  fields: readonly InputSchemaField[],
  parent?: TaskColumnFieldOption,
): TaskColumnFieldOption[] {
  return fields.flatMap((field) => {
    const option: TaskColumnFieldOption = {
      path: parent ? `${parent.path}.${field.fieldName}` : field.fieldName,
      label: parent ? `${parent.label} › ${field.label}` : field.label,
      format: field.isMultiple ? undefined : inputFieldFormat(field.fieldType),
    };
    const children =
      field.children && !field.isMultiple
        ? inputSchemaFieldOptions(field.children, option)
        : [];
    return [option, ...children];
  });
}

export function outcomeFieldOptions(
  fields: readonly TaskOutcomeField[],
): TaskColumnFieldOption[] {
  return fields.map((field) => ({
    path: field.key,
    label: field.label,
    format:
      field.type === 'number'
        ? 'number'
        : field.type === 'date'
          ? 'date'
          : undefined,
  }));
}

export function anchorFieldOptions(
  entries: readonly TaskAnchorFieldCatalogEntry[],
): TaskColumnFieldOption[] {
  return entries.map((entry) => ({
    path: entry.path,
    label: humanizeColumnPath(entry.path),
    format:
      entry.type === 'number' || entry.type === 'decimal'
        ? 'number'
        : entry.type === 'date'
          ? 'date'
          : undefined,
  }));
}

type FieldOptionSources = {
  taskType: Pick<TaskTypeResponse, 'inputSchema' | 'outcomeFields'> | undefined;
  anchorCatalog: readonly TaskAnchorFieldCatalogEntry[];
};

const FIELD_OPTION_BUILDERS: Record<
  TaskColumnSource,
  (sources: FieldOptionSources) => TaskColumnFieldOption[]
> = {
  input: ({ taskType }) =>
    inputSchemaFieldOptions(taskType?.inputSchema?.fields ?? []),
  correction: ({ taskType }) =>
    inputSchemaFieldOptions(taskType?.inputSchema?.fields ?? []),
  outcomeData: ({ taskType }) =>
    outcomeFieldOptions(taskType?.outcomeFields ?? []),
  metadata: () => [],
  anchor: ({ anchorCatalog }) => anchorFieldOptions(anchorCatalog),
};

/**
 * Schema-backed suggestions for a picker source: the type's input schema
 * (input + correction), its outcome fields, or the anchor field catalog.
 * Metadata has no schema, so it is free-text only.
 */
export function fieldOptionsForSource(
  source: TaskColumnSource,
  sources: FieldOptionSources,
): TaskColumnFieldOption[] {
  return FIELD_OPTION_BUILDERS[source](sources);
}

/** `supplier.invoiceNumber` → "Invoice number" (last segment, sentence case). */
export function humanizeColumnPath(path: string): string {
  const last = path.split('.').filter(Boolean).at(-1) ?? path;
  const words = last
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The column fields a picker format is stored as. */
function storedFormat(
  format: ColumnFormat | undefined,
): Pick<ColumnConfigEntry, 'format' | 'dateStyle'> {
  if (!format) return {};
  if (format === 'date_medium') return { format: 'date', dateStyle: 'medium' };
  return { format };
}

/** The format a column displays with, folding `dateStyle` into `date_medium`. */
export function columnDisplayFormat(
  entry: Pick<ColumnConfigEntry, 'format' | 'dateStyle'>,
): ColumnFormat | undefined {
  return entry.format === 'date' && entry.dateStyle === 'medium'
    ? 'date_medium'
    : entry.format;
}

/** Column entry (minus id) for a picked source + path. Never sortable. */
export function buildPickedColumn({
  source,
  path,
  header,
  format,
}: {
  source: TaskColumnSource;
  path: string;
  header: string;
  format?: ColumnFormat;
}): Omit<ColumnConfigEntry, 'id'> {
  return {
    header: header.trim() || humanizeColumnPath(path),
    apiField: buildTaskColumnApiField(source, path),
    sortable: false,
    ...storedFormat(format),
    ...(format && NUMERIC_FORMATS.has(format) ? { alignRight: true } : {}),
  };
}

/** Same id shape `useColumnConfig.addColumn` generates for custom columns. */
export function newCustomColumnId(): string {
  return `custom-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

/** Columns created through the picker (removable, unlike built-ins). */
export function isPathColumn(entry: ColumnConfigEntry): boolean {
  return parseTaskColumnPath(entry.apiField) !== null;
}

/**
 * Anchor paths `tasks.list` must return for the visible columns: every
 * visible `anchor.*` column plus, when the anchor badge column is shown, the
 * entity type's label fields.
 */
export function collectAnchorFields(
  columns: readonly ColumnConfigEntry[],
  anchorEntityType: SystemEntity | null | undefined,
): string[] {
  const paths = new Set<string>();
  for (const column of columns) {
    if (column.hidden) continue;
    if (column.id === 'anchor' && anchorEntityType) {
      for (const field of ANCHOR_LABEL_FIELDS[anchorEntityType] ?? []) {
        paths.add(field);
      }
      continue;
    }
    const parsed = parseTaskColumnPath(column.apiField);
    if (parsed?.source === 'anchor') paths.add(parsed.segments.join('.'));
  }
  return [...paths].slice(0, MAX_TASK_ANCHOR_FIELDS);
}
